import { UserContextState } from "@src/lib/contexts/UserContext";
import { useIntercom } from "react-use-intercom";
import { useEffect, useLayoutEffect, useRef } from "react";
import { generateHMAC } from "@src/lib/utils/hmac";
import { useRouter } from "next/router";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { useImpersonation } from "@src/features/app/hooks/useImpersonation";
import { Ff, UserType } from "@src/types.generated";
import useOpenIntercom, {
  OpenIntercom,
} from "@src/features/app/hooks/useOpenIntercom";

const useUpdateIntercom = (userContext?: UserContextState): OpenIntercom => {
  const {
    boot: bootIntercom,
    update: updateIntercom,
    hide: hideIntercom,
    shutdown: shutdownIntercom,
    trackEvent,
  } = useIntercom();
  const { isImpersonating } = useImpersonation();
  const anonymizedRef = useRef(false);
  const router = useRouter();
  const chatKnowledgeBaseEnabled = useFeatureFlag(Ff.ChatKnowledgeBase);
  const opusAgentEnabled = useFeatureFlag(Ff.OpusAgent);
  // Read from the argument: AuthedApp calls this above its UserContext
  // provider, where useUser() would still be empty.
  const isFullAdmin = userContext?.user?.userType === UserType.Admin;
  const hideDefaultLauncher =
    chatKnowledgeBaseEnabled ||
    (opusAgentEnabled && isFullAdmin) ||
    router.pathname.includes("ask-opus");

  useEffect(() => {
    if (
      userContext?.user &&
      process.env.NEXT_PUBLIC_VERCEL_ENV === "production" &&
      !userContext.isStaff &&
      !isImpersonating
    ) {
      const payload = {
        userId: userContext.user.id.toString(),
        userHash: generateHMAC(
          process.env.NEXT_PUBLIC_INTERCOM_SECRET_KEY!,
          userContext.user.id.toString(),
        ),
        email: userContext.user.email || undefined,
        name: userContext.user.name!,
        company: {
          companyId: userContext.user.org!.id.toString(),
          name: userContext.user.org?.name,
        },
      };
      updateIntercom(payload);
      trackEvent("dashboard-loaded");
      hideIntercom();
    } else if (
      userContext?.user &&
      process.env.NEXT_PUBLIC_VERCEL_ENV === "production" &&
      !anonymizedRef.current
    ) {
      anonymizedRef.current = true;
      shutdownIntercom();
      bootIntercom({ hideDefaultLauncher });
    }
  }, [userContext]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    // Hide the default launcher wherever Ask Opus is present (the KB chat, or
    // the agent panel for admins on the Opus Agent); Intercom stays reachable
    // through the support dropdown.
    updateIntercom({ hideDefaultLauncher });
  }, [updateIntercom, hideDefaultLauncher]);

  return useOpenIntercom(userContext);
};

export default useUpdateIntercom;
